import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, Pencil, Layers, Users, Factory, Upload } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button, Badge } from '../../components/ui';
import { STAT_CARD_BASE } from '../../components/ui/StatCard';
import ImportSimulationModal from '../../components/simulation/ImportSimulationModal';
import simulationService from '../../services/simulationService';
import { showError } from '../../utils/toast';

const SimulationCardSkeleton = () => (
  <div className={`${STAT_CARD_BASE} animate-pulse p-5`}>
    <div className="h-4 w-1/3 rounded bg-neutral-200 dark:bg-neutral-800" />
    <div className="mt-3 h-3 w-2/3 rounded bg-neutral-100 dark:bg-neutral-800" />
    <div className="mt-5 flex gap-2">
      <div className="h-6 w-20 rounded-lg bg-neutral-100 dark:bg-neutral-800" />
      <div className="h-6 w-20 rounded-lg bg-neutral-100 dark:bg-neutral-800" />
    </div>
  </div>
);

const TrainerSimulationsPage = () => {
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await simulationService.list();
        if (alive) setSimulations(res.simulations || []);
      } catch (err) {
        showError(err.userMessage || 'Could not load your simulations');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <DashboardLayout title="Simulations">
      <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto pb-20 sm:pb-6 space-y-5">
        {/* ---------- header ---------- */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="max-w-2xl text-sm text-neutral-500 dark:text-neutral-400">
            Role-play exercises your participants run in teams during a live session. Each team
            gets its own decisions, and every choice moves the numbers the business is judged on.
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              <Upload size={16} strokeWidth={2} className="mr-1.5" />
              Import
            </Button>
            <Button variant="primary-filled" onClick={() => navigate('/trainer/simulations/new')}>
              <Plus size={16} strokeWidth={2} className="mr-1.5" />
              New simulation
            </Button>
          </div>
        </div>

        {/* ---------- list ---------- */}
        {loading ? (
          <div className="space-y-3">
            <SimulationCardSkeleton />
          </div>
        ) : simulations.length === 0 ? (
          <div className={`${STAT_CARD_BASE} flex flex-col items-center justify-center px-6 py-14 text-center`}>
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-500 dark:bg-primary-500/10">
              <Layers size={20} strokeWidth={1.75} />
            </span>
            <h3 className="mt-3 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              No simulations yet
            </h3>
            <p className="mt-1 max-w-sm text-xs text-neutral-500 dark:text-neutral-400">
              Describe what you want people to practise and we&apos;ll draft the scenarios, the
              teams and their decisions. You edit everything before it goes live.
            </p>
            <div className="mt-4 flex gap-2">
              <Button variant="primary" onClick={() => navigate('/trainer/simulations/new')}>
                Create your first simulation
              </Button>
              <Button variant="secondary" onClick={() => setImportOpen(true)}>
                Import from Word &amp; Excel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {simulations.map((sim) => (
              <div key={sim.id} className={`${STAT_CARD_BASE} p-5`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                        {sim.title}
                      </h3>
                      <Badge
                        variant={sim.awaitingGeneration ? 'warning' : sim.status === 'published' ? 'success' : 'default'}
                        size="sm"
                      >
                        {sim.awaitingGeneration
                          ? 'Needs scenarios'
                          : sim.status === 'published'
                          ? 'Ready to run'
                          : 'Draft'}
                      </Badge>
                    </div>
                    {sim.subtitle && (
                      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{sim.subtitle}</p>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                      <span className="inline-flex items-center gap-1.5">
                        <Factory size={13} strokeWidth={1.75} />
                        {sim.industry}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Layers size={13} strokeWidth={1.75} />
                        {sim.roundCount} rounds
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Users size={13} strokeWidth={1.75} />
                        {sim.functionCount} teams
                      </span>
                      <span className="tabular-nums">{sim.cardCount} decisions</span>
                    </div>
                  </div>

                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/trainer/simulations/${sim.id}`)}
                    >
                      <Pencil size={14} strokeWidth={2} className="mr-1.5" />
                      Edit
                    </Button>
                    <Button
                      variant="primary-filled"
                      size="sm"
                      disabled={sim.awaitingGeneration}
                      title={sim.awaitingGeneration ? 'Needs scenarios before it can be played' : undefined}
                      onClick={() => navigate(`/trainer/simulations/${sim.id}/rehearse`)}
                    >
                      <Play size={14} strokeWidth={2} className="mr-1.5" />
                      Rehearse
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <ImportSimulationModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={(sim) => { setImportOpen(false); navigate(`/trainer/simulations/${sim.id}`); }}
        />
      </div>
    </DashboardLayout>
  );
};

export default TrainerSimulationsPage;
